/**
 * Public Tenant API client.
 * Talks to red-reaiz-api `/public/*` endpoints — no auth required.
 *
 * Tenant resolution order (in browser):
 *   1. ?tenant=<slug> in URL (dev/preview override)
 *   2. Current window.location.host (prod)
 *
 * Usage:
 *   const tenant = await publicTenantApi.resolveCurrent();
 *   applyThemeCssVars(tenant.theme);
 */

import i18n from '@/lib/i18n';

const API_BASE =
  import.meta.env.VITE_PUBLIC_API_URL ||
  import.meta.env.VITE_API_URL?.replace(/\/$/, '') ||
  'https://api.redraiz.co/api/v1';

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  // Idioma de contenido: la API resuelve el contenido traducible (títulos,
  // descripciones, secciones, features…) a este idioma con fallback a ES. Va
  // como ?lang= porque el navegador PROHÍBE fijar Accept-Language por JS.
  const url = new URL(`${API_BASE}${path}`);
  if (!url.searchParams.has('lang')) {
    url.searchParams.set('lang', (i18n.language || 'es').split('-')[0]);
  }
  const res = await fetch(url.toString(), {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${text.slice(0, 200)}`);
  }
  const json = await res.json();
  return (json?.data ?? json) as T;
}

export interface PublicTenantTheme {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  themeMode: string;
  themeStyle: string;
  customCSS?: string | null;
  /** Compiled per-tenant theme CSS URL served from Firebase Storage / CDN. */
  cssUrl?: string | null;
  /** Increments on each regeneration — used for cache-busting if needed. */
  version?: number;
}

export interface PublicTenantProperty {
  id: string;
  title: string;
  slug?: string | null;
  price: number | null;
  currency: string;
  type: string;
  operation: string;
  /** Estado de la propiedad. SOLD/RENTED llegan durante los 7 días post-cierre. */
  status?: 'AVAILABLE' | 'RESERVED' | 'SOLD' | 'RENTED' | 'UNAVAILABLE';
  bedrooms?: number | null;
  bathrooms?: number | null;
  area?: number | null;
  builtArea?: number | null;
  city?: string | null;
  neighborhood?: string | null;
  province?: string | null;
  country?: string | null;
  countryCode?: string | null;
  postalCode?: string | null;
  formattedAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  images: string[];
  videos?: string[];
  features: string[];
  description?: string | null;
  publishedAt?: string | null;
  updatedAt?: string | null;
  featured?: boolean;
  /** E8 — URL del vídeo IA cuando está listo. */
  aiVideoUrl?: string | null;
  aiVideoStatus?: 'NONE' | 'QUEUED' | 'PROCESSING' | 'READY' | 'FAILED';
  // E11 — ficha técnica
  conservationState?: string | null;
  yearBuilt?: number | null;
  floor?: string | null;
  hasElevator?: boolean | null;
  furnished?: 'yes' | 'no' | 'semi' | null;
  heating?: string | null;
  hotWater?: string | null;
  garageType?: 'none' | 'private' | 'communal' | null;
  rentalDuration?: 'long' | 'short' | 'temporal' | null;
  energyRating?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | null;
  energyEmissionsRating?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | null;
  energyConsumption?: number | null;
  energyEmissions?: number | null;
  legalNotice?: string | null;
}

export interface PublicTenant {
  id: string;
  slug: string;
  name: string;
  tagline?: string | null;
  description?: string | null;
  logo?: string | null;
  favicon?: string | null;
  theme: PublicTenantTheme;
  contact: {
    phone?: string | null;
    email?: string | null;
    address?: string | null;
    whatsapp?: string | null;
  };
  social: Record<string, string | null | undefined>;
  seo: {
    title?: string | null;
    description?: string | null;
    keywords?: string[];
    ogImage?: string | null;
  };
  analytics: {
    googleAnalytics?: string | null;
    googleTagManager?: string | null;
    facebookPixel?: string | null;
  };
  features: {
    blogEnabled: boolean;
    virtualToursEnabled: boolean;
    propertyComparisonEnabled: boolean;
  };
  currencies: string[];
  languages: string[];
  /** E10 — tipo de interés anual usado por el simulador de hipoteca. */
  defaultMortgageRate?: number | null;
  page: {
    id: string;
    title: string;
    slug: string;
    sections: Array<{
      id: string;
      type: string;
      title?: string | null;
      subtitle?: string | null;
      content?: any;
      order: number;
    }>;
  } | null;
  featuredProperties: PublicTenantProperty[];
  recentBlog: Array<{
    id: string;
    title: string;
    slug: string;
    excerpt?: string | null;
    coverImage?: string | null;
    publishedAt: string;
  }>;
}

export interface PublicSlotsDay {
  date: string;
  weekday: string;
  slots: Array<{ start: string; scheduledAt: string }>;
}

export const publicTenantApi = {
  /**
   * Resolve the tenant for the current browser window.
   */
  async resolveCurrent(): Promise<PublicTenant> {
    // 1) ?tenant=<slug> query param (iframe / dev override)
    const override = new URLSearchParams(window.location.search).get('tenant');
    if (override) {
      return http<PublicTenant>(`/public/tenant/by-slug/${encodeURIComponent(override)}`);
    }

    // 2) En prod: subdominio via by-host
    const host = window.location.host;
    const isLocal = host.startsWith('localhost') || host.startsWith('127.0.0.1');
    if (!isLocal) {
      return http<PublicTenant>(`/public/tenant/by-host?host=${encodeURIComponent(host)}`);
    }

    // 3) Dev local sin ?tenant: usar VITE_TENANT o fallback
    const envTenant = import.meta.env.VITE_TENANT as string | undefined;
    const slug = envTenant || 'elite';
    return http<PublicTenant>(`/public/tenant/by-slug/${encodeURIComponent(slug)}`);
  },

  async getBySlug(slug: string): Promise<PublicTenant> {
    return http<PublicTenant>(`/public/tenant/by-slug/${encodeURIComponent(slug)}`);
  },

  async listProperties(companyId: string, filters: Record<string, string | number | undefined> = {}) {
    const query = new URLSearchParams({ companyId });
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
    });
    return http<{
      items: PublicTenantProperty[];
      total: number;
      page: number;
      pageSize: number;
      pages: number;
    }>(`/public/properties?${query.toString()}`);
  },

  async getProperty(companyId: string, idOrSlug: string): Promise<PublicTenantProperty> {
    const q = new URLSearchParams({ companyId }).toString();
    return http<PublicTenantProperty>(`/public/properties/${encodeURIComponent(idOrSlug)}?${q}`);
  },

  async listBlogPosts(
    companyId: string,
    filters: Record<string, string | number | boolean | undefined> = {},
  ) {
    const query = new URLSearchParams({ companyId });
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
    });
    return http<{
      items: Array<{
        id: string;
        title: string;
        slug: string;
        excerpt?: string | null;
        featuredImage?: string | null;
        category: string;
        tags: string[];
        featured: boolean;
        readTime?: number | null;
        publishedAt: string;
        author?: { id: string; name: string; avatar?: string | null } | null;
      }>;
      total: number;
      page: number;
      pageSize: number;
      pages: number;
    }>(`/public/blog?${query.toString()}`);
  },

  async getBlogPost(companyId: string, slug: string) {
    const q = new URLSearchParams({ companyId }).toString();
    return http<{
      id: string;
      title: string;
      slug: string;
      excerpt?: string | null;
      content: string;
      featuredImage?: string | null;
      category: string;
      tags: string[];
      readTime?: number | null;
      seoTitle?: string | null;
      seoDescription?: string | null;
      seoKeywords: string[];
      publishedAt: string;
      author?: { id: string; name: string; avatar?: string | null } | null;
    }>(`/public/blog/${encodeURIComponent(slug)}?${q}`);
  },

  async getSlots(companyId: string, propertyId: string, days = 5): Promise<PublicSlotsDay[]> {
    const q = new URLSearchParams({ companyId, propertyId, days: String(days) }).toString();
    return http<PublicSlotsDay[]>(`/public/appointments/slots?${q}`);
  },

  async bookAppointment(body: {
    companyId: string;
    propertyId: string;
    leadName: string;
    leadPhone?: string;
    leadEmail?: string;
    scheduledAt: string;
    notes?: string;
  }): Promise<{ leadId: string; appointmentId: string; scheduledAt: string }> {
    return http('/public/appointments', { method: 'POST', body: JSON.stringify(body) });
  },

  async createLead(body: {
    companyId: string;
    name: string;
    phone?: string;
    email?: string;
    message?: string;
    propertyId?: string;
  }): Promise<{ id: string }> {
    return http('/public/leads', { method: 'POST', body: JSON.stringify(body) });
  },

  async createOwnerLead(body: {
    companyId: string;
    name: string;
    phone?: string;
    email?: string;
    message?: string;
    operation: 'VENTA' | 'ARRIENDO' | 'ARRIENDO_TEMPORAL';
    propertyType: 'CASA' | 'DEPARTAMENTO' | 'OFICINA' | 'TERRENO' | 'LOCAL' | 'BODEGA';
    address?: string;
    zone?: string;
    bedrooms?: number;
    area?: number;
    expectedPrice?: number;
    expectedRent?: number;
    wantsAppraisal?: boolean;
  }): Promise<{ leadId: string }> {
    return http('/public/property-leads', { method: 'POST', body: JSON.stringify(body) });
  },

  async createAppraisal(body: {
    companyId: string;
    operation: 'VENTA' | 'ARRIENDO' | 'ARRIENDO_TEMPORAL';
    propertyType: 'CASA' | 'DEPARTAMENTO' | 'OFICINA' | 'TERRENO' | 'LOCAL' | 'BODEGA';
    address?: string;
    zone?: string;
    city?: string;
    postalCode?: string;
    bedrooms?: number;
    bathrooms?: number;
    area: number;
    yearBuilt?: number;
    condition?: 'NEW' | 'GOOD' | 'REFORM' | 'TO_REFORM';
    contactName: string;
    contactEmail?: string;
    contactPhone?: string;
  }): Promise<{
    success: boolean;
    data: {
      id: string;
      leadId: string | null;
      status: string;
      estimateMin: number | null;
      estimateMax: number | null;
      estimatePerM2: number | null;
      currency: string;
      rationale: string | null;
      operation: string;
    };
  }> {
    return http('/public/appraisals', { method: 'POST', body: JSON.stringify(body) });
  },

  async createMortgageLead(body: {
    companyId: string;
    propertyId: string;
    name: string;
    phone?: string;
    email?: string;
    message?: string;
    simulation: {
      price: number;
      downPayment: number;
      years: number;
      annualRate: number;
      monthlyPayment: number;
      totalPaid: number;
      totalInterest: number;
    };
  }): Promise<{ leadId: string }> {
    return http('/public/mortgage-leads', { method: 'POST', body: JSON.stringify(body) });
  },
};

/**
 * Convert a `#rrggbb` hex color to `H S% L%` triplet used by Tailwind's
 * `hsl(var(--primary))` pattern. Returns null when the input is not a hex.
 */
function hexToHslTriplet(hex: string): string | null {
  const clean = hex.trim().replace('#', '');
  if (!/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(clean)) return null;
  const full = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Apply the tenant theme.
 *
 * Preferred path: inject the per-tenant precompiled CSS <link> from the CDN
 * (Firebase Storage). This brings primary/secondary/accent scales (50..900)
 * as CSS variables, which Tailwind maps via `hsl(var(--primary))` etc.
 *
 * Fallback path (dev / company without compiled CSS yet): set base vars
 * inline so the app doesn't stay white. Also emits the HSL triplets Tailwind
 * expects so `bg-primary`, `text-primary`, etc. resolve to the tenant color
 * without needing the compiled CSS.
 */
export function applyThemeCssVars(theme: PublicTenantTheme) {
  const root = document.documentElement;
  root.setAttribute('data-theme-mode', theme.themeMode);
  root.setAttribute('data-theme-style', theme.themeStyle);

  if (theme.cssUrl) {
    const versioned = theme.version ? `${theme.cssUrl}?v=${theme.version}` : theme.cssUrl;
    let linkEl = document.getElementById('tenant-theme') as HTMLLinkElement | null;
    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.id = 'tenant-theme';
      linkEl.rel = 'stylesheet';
      document.head.appendChild(linkEl);
    }
    if (linkEl.href !== versioned) linkEl.href = versioned;
  } else {
    root.style.setProperty('--tenant-primary', theme.primaryColor);
    root.style.setProperty('--tenant-secondary', theme.secondaryColor);
    root.style.setProperty('--tenant-accent', theme.accentColor);
  }

  // Brand colors (source of truth) are always derived from the tenant hex and
  // applied inline with `important`. This guarantees the visible accent tokens
  // (`bg-primary`, badges, buttons…) match the company brand even when the
  // precompiled CDN CSS is stale/auto-generated with off-brand defaults
  // (e.g. a tenant shipped with purple primary + dark color-scheme while its
  // real brand is navy + light). Inline !important wins over the <link>.
  const primaryHsl = hexToHslTriplet(theme.primaryColor);
  const secondaryHsl = hexToHslTriplet(theme.secondaryColor);
  const accentHsl = hexToHslTriplet(theme.accentColor);
  if (primaryHsl) {
    root.style.setProperty('--primary', primaryHsl, 'important');
    root.style.setProperty('--primary-foreground', '0 0% 100%', 'important');
    root.style.setProperty('--ring', primaryHsl, 'important');
  }
  if (secondaryHsl) {
    root.style.setProperty('--secondary', secondaryHsl, 'important');
    root.style.setProperty('--secondary-foreground', '0 0% 100%', 'important');
  }
  if (accentHsl) {
    root.style.setProperty('--accent', accentHsl, 'important');
    root.style.setProperty('--accent-foreground', '0 0% 100%', 'important');
  }
  // The landing has no real dark mode: every page/section assumes a light
  // surface. A precompiled tenant CSS generated with themeMode=dark (e.g.
  // elite) flips --background/--foreground and breaks sections that use
  // bg-background (Servicios, Quiénes Somos...) into unreadable dark panels.
  // Pin the neutral surface tokens to the light palette inline (!important
  // wins over the CDN <link>), mirroring what we already do for brand colors.
  const LIGHT_SURFACE_TOKENS: Record<string, string> = {
    '--background': '0 0% 100%',
    '--foreground': '0 0% 3.9%',
    '--card': '0 0% 100%',
    '--card-foreground': '0 0% 3.9%',
    '--popover': '0 0% 100%',
    '--popover-foreground': '0 0% 3.9%',
    '--muted': '0 0% 96.1%',
    '--muted-foreground': '0 0% 45.1%',
    '--border': '0 0% 89.8%',
    '--input': '0 0% 89.8%',
  };
  Object.entries(LIGHT_SURFACE_TOKENS).forEach(([token, value]) => {
    root.style.setProperty(token, value, 'important');
  });
  // Always light: native controls (selects/inputs) must not render dark even
  // when a stale theme declares themeMode=dark.
  root.style.setProperty('color-scheme', 'light');

  if (theme.customCSS) {
    let styleEl = document.getElementById('tenant-custom-css') as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'tenant-custom-css';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = theme.customCSS;
  }
}

// #333 — Inyección de códigos de medición del tenant (GA4/UA, GTM, Facebook
// Pixel). Los códigos ya llegaban en `tenant.analytics` pero no se renderizaban:
// medición muerta. Se inyectan una sola vez (idempotente por id) tras resolver
// el tenant, con guarda de formato como defensa en profundidad: aunque el
// backend ya valida al guardar, datos antiguos pudieron persistirse con el
// validador laxo, así que un valor corrupto no debe inyectar <script> arbitrario.
const GA_RE = /^(G-[A-Z0-9]{10}|UA-\d+-\d+)$/;
const GTM_RE = /^GTM-[A-Z0-9]{7}$/;
const PIXEL_RE = /^\d{15,16}$/;

export function applyTenantAnalytics(analytics?: PublicTenant['analytics'] | null) {
  if (!analytics || typeof document === 'undefined') return;

  const ga = (analytics.googleAnalytics || '').trim();
  const gtm = (analytics.googleTagManager || '').trim();
  const pixel = (analytics.facebookPixel || '').trim();

  // Google Tag Manager
  if (GTM_RE.test(gtm) && !document.getElementById('tenant-gtm')) {
    const s = document.createElement('script');
    s.id = 'tenant-gtm';
    s.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtm}');`;
    document.head.appendChild(s);
  }

  // Google Analytics 4 / Universal (gtag.js)
  if (GA_RE.test(ga) && !document.getElementById('tenant-ga')) {
    const loader = document.createElement('script');
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${ga}`;
    document.head.appendChild(loader);
    const init = document.createElement('script');
    init.id = 'tenant-ga';
    init.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${ga}');`;
    document.head.appendChild(init);
  }

  // Facebook Pixel
  if (PIXEL_RE.test(pixel) && !document.getElementById('tenant-fb-pixel')) {
    const s = document.createElement('script');
    s.id = 'tenant-fb-pixel';
    s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixel}');fbq('track','PageView');`;
    document.head.appendChild(s);
  }
}
